'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { Button, Icons, LoadingIndicator } from '@commenda/kakashi';
import { CountryCodeMap } from '@commenda/kakashi/src/components/CountryInput/countryCodeMap';
import { FormsTemplateType } from '@commenda/types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useComponentRegistry } from '../../hooks/ComponentRegistryContext';
import { FormContext } from '../../hooks/FormContext';
import type { DSLFile, FormComponent } from '@commenda/types';
import {
  buildComponentMap,
  flattenComponents,
} from '../../utils/dsl-traversal';
import { LinkedFormRenderer } from './LinkedFormRenderer';
import { NestedFormDrawer } from './NestedFormDrawer';

export const NestedFormComponent = ({
  fieldId,
  label,
  description,
  props,
}: {
  fieldId?: string;
  label?: string;
  description?: string;
  props?: Record<string, unknown>;
}) => {
  const [open, setOpen] = useState(false);
  // null = adding a new entry; number = editing an existing entry at that index
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const {
    formData,
    updateField,
    errors,
    setErrors,
    formTemplateId,
    formTemplateKey,
    nestedFormOpen,
    setNestedFormOpen,
  } = useContext(FormContext);
  const componentRegistry = useComponentRegistry();

  const keyId = props?.keyId as string | undefined;
  // `displayFieldIds` names an entry with several of the linked form's fields
  // (first + last name reads like a person; either alone does not). Older
  // templates carry the single `displayFieldId`, which still applies.
  const displayFieldIds = useMemo(() => {
    const configured = props?.displayFieldIds ?? props?.displayFieldId;
    const ids = Array.isArray(configured) ? configured : [configured];
    return ids.filter(
      (id): id is string => typeof id === 'string' && id !== ''
    );
  }, [props?.displayFieldIds, props?.displayFieldId]);
  const buttonText = (props?.buttonText as string) || 'Add';
  const max =
    props?.max != null && props.max !== '' ? Number(props.max) : undefined;

  // Fetch key & template eagerly (not just when open) so the label map
  // is always available in handleSubmit regardless of timing.
  const { key: templateKey, isLoading: isLoadingKey } = useFormTemplateKey(
    keyId ?? null
  );

  const {
    template,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useFormsTemplate(templateKey?.liveFormId ?? null);

  // Only surface the loading state while the drawer is actually open
  const isLoading = open && (isLoadingKey || isLoadingTemplate);

  // Entries are stored as an array
  const entries = fieldId
    ? Array.isArray(formData[fieldId])
      ? (formData[fieldId] as Record<string, unknown>[])
      : []
    : [];

  const isAtMax = max !== undefined && entries.length >= max;
  const minError = fieldId ? (errors[fieldId] ?? null) : null;

  // Sync local open/editingIndex from persisted context (e.g. after refresh)
  useEffect(() => {
    if (nestedFormOpen?.fieldId === fieldId) {
      let index: number | null = nestedFormOpen.editingIndex;
      if (
        index !== null &&
        (index < 0 || index >= entries.length || entries[index] === undefined)
      ) {
        index = null; // Fall back to add mode if entry no longer exists
      }
      setEditingIndex(index);
      setOpen(true);
    } else {
      setOpen(false);
      setEditingIndex(null);
    }
  }, [
    nestedFormOpen?.fieldId,
    nestedFormOpen?.editingIndex,
    fieldId,
    entries.length,
  ]);

  // Resolve a field value to its display label using the component definition
  const resolveDisplayValue = (
    val: unknown,
    comp?: FormComponent
  ): string | null => {
    if (val == null || val === '') return null;

    // CountryInput: resolve code to country name
    if (comp?.component === 'CountryInput' && typeof val === 'string') {
      return CountryCodeMap[val as keyof typeof CountryCodeMap] || val;
    }

    // Select / RadioGroup: resolve value to option label
    if (
      (comp?.component === 'Select' || comp?.component === 'RadioGroup') &&
      comp.options?.length
    ) {
      const option = comp.options.find(
        (opt) => String(opt.value) === String(val)
      );
      if (option?.label) return option.label;
    }

    // CheckboxGroup: resolve selected values to labels
    if (
      comp?.component === 'CheckboxGroup' &&
      comp.options?.length &&
      typeof val === 'object' &&
      !Array.isArray(val)
    ) {
      const obj = val as Record<string, boolean>;
      const labels = Object.entries(obj)
        .filter(([, selected]) => selected)
        .map(([optVal]) => {
          const option = comp.options!.find(
            (opt) => String(opt.value) === optVal
          );
          return option?.label || optVal;
        });
      if (labels.length > 0) return labels.join(', ');
    }

    if (typeof val === 'string' && val.trim()) return val.trim();
    if (typeof val === 'number') return String(val);
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    return null;
  };

  // Walks entry fields and returns the first one that yields a printable label.
  // Fields that exist but can't produce a label (e.g. generic objects, empty
  // file uploads) are skipped so a meaningful sibling field can be used instead.
  const tryGetEntryLabel = ([key, val]: [string, unknown]): string | null => {
    if (key === '__meta') return null;
    if (val === null || val === undefined || val === '') return null;

    // File instance (e.g. newly selected but not yet uploaded)
    if (val instanceof File) return val.name;

    if (typeof val === 'object' && !Array.isArray(val)) {
      const obj = val as Record<string, unknown>;

      // File upload object { fileName, fileUrl, ... }
      const fileKeys = ['fileName', 'fileUrl', 'fileId', 'documentType'];
      if (fileKeys.some((k) => k in obj)) {
        const { fileName } = obj;
        return fileName != null && fileName !== '' ? String(fileName) : null;
      }

      // Address object
      const addressKeys = [
        'addressLine1',
        'addressLine2',
        'postalCode',
        'city',
        'state',
        'country',
      ];
      if (addressKeys.some((k) => k in obj)) {
        const parts = ['addressLine1', 'city', 'state', 'country']
          .map((k) => obj[k])
          .filter((v) => v != null && v !== '');
        return parts.length > 0 ? parts.map(String).join(', ') : null;
      }

      // Generic object: skip (don't show JSON)
      return null;
    }

    // Multi-file upload array
    if (Array.isArray(val)) {
      if (val.length === 0) return null;
      const first = val[0];
      if (first instanceof File) return first.name;
      if (
        typeof first === 'object' &&
        first !== null &&
        'fileName' in (first as Record<string, unknown>)
      ) {
        const { fileName } = first;
        return fileName != null && fileName !== '' ? String(fileName) : null;
      }
      return null;
    }

    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'string' && val.trim()) return val.trim();
    if (typeof val === 'number') return String(val);
    return null;
  };

  // Returns a display label for an entry's first meaningful field value.
  const getEntryLabel = (entry: Record<string, unknown>): string => {
    // Build component map from the linked template for value resolution.
    // template.dsl is FormDSL, which shares the steps/modules/components shape
    // the traversal walks, so the cast is safe (same as in handleSubmit).
    const compMap = template?.dsl
      ? buildComponentMap(template.dsl as unknown as DSLFile)
      : {};

    // Use configured display fields if available
    if (displayFieldIds.length > 0) {
      const parts = displayFieldIds
        .map((id) => resolveDisplayValue(entry[id], compMap[id]))
        .filter((part): part is string => part != null);
      if (parts.length > 0) return parts.join(' ');
    }

    // Auto-detect walks the linked form's own field order. An entry's key
    // order is the order the filler happened to touch the fields, so walking
    // that instead labels the row with whatever they typed first.
    const orderedFieldIds = template?.dsl
      ? flattenComponents(template.dsl as unknown as DSLFile)
          .map((component) => component.fieldId)
          .filter((id): id is string => id != null && id !== '')
      : [];

    // Before the linked template resolves there is no order to walk, so keep
    // labelling from the entry itself rather than flashing "Entry 1".
    const entryLabel =
      orderedFieldIds.length > 0
        ? orderedFieldIds
            .map((id) => tryGetEntryLabel([id, entry[id]]))
            .find((s): s is string => s != null)
        : Object.entries(entry)
            .map(tryGetEntryLabel)
            .find((s): s is string => s != null);

    return entryLabel ?? `Entry ${entries.indexOf(entry) + 1}`;
  };

  const openAdd = () => {
    if (fieldId) setNestedFormOpen({ fieldId, editingIndex: null });
    setEditingIndex(null);
    setOpen(true);
  };

  const openEdit = (index: number) => {
    if (fieldId) setNestedFormOpen({ fieldId, editingIndex: index });
    setEditingIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setNestedFormOpen(null);
    setOpen(false);
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    if (!fieldId) return;
    const newEntries = entries.filter((_, i) => i !== index);
    updateField(fieldId, newEntries);
  };

  const handleSubmit = (data: Record<string, unknown>) => {
    if (!fieldId) return;

    // Build fieldId → label map from the nested form's component map.
    // buildComponentMap works on DSLFile; template.dsl is FormDSL which shares
    // the same steps/modules/components shape, so the cast is safe.
    const labelMap: Record<string, string> = {};
    if (template?.dsl) {
      const compMap = buildComponentMap(template.dsl as unknown as DSLFile);
      Object.entries(compMap).forEach(([fid, comp]) => {
        if (comp.label) labelMap[fid] = comp.label;
      });
    }

    const entryWithMeta: Record<string, unknown> = {
      ...data,
      __meta: { labels: labelMap, formName: template?.name ?? label ?? '' },
    };

    if (editingIndex !== null) {
      const newEntries = entries.map((e, i) =>
        i === editingIndex ? entryWithMeta : e
      );
      updateField(fieldId, newEntries);
    } else {
      updateField(fieldId, [...entries, entryWithMeta]);
    }
    // Clear min-count error now that the user has added/updated an entry
    const { [fieldId]: _removed, ...restErrors } = errors;
    setErrors(restErrors);
    handleClose();
  };

  // Strip __meta when pre-filling the drawer for editing
  const defaultValues = useMemo(() => {
    if (editingIndex !== null && entries[editingIndex]) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __meta: _ignored, ...rest } = entries[editingIndex];
      return rest;
    }
    return {};
  }, [editingIndex, entries]);

  if (!keyId) {
    return null;
  }

  // Hide when the nested form is the same as the current form (self-reference in preview).
  // Check 1: exact live-version ID match (works when previewing the live version).
  if (
    formTemplateId != null &&
    templateKey?.liveFormId != null &&
    templateKey.liveFormId === formTemplateId
  ) {
    return null;
  }
  // Check 2: key-string match — catches the case where the current form is a draft
  // (formTemplateId differs from the key's liveFormId, but both belong to the same
  // form family identified by the version-agnostic key string).
  if (
    formTemplateKey != null &&
    templateKey?.key != null &&
    templateKey.key === formTemplateKey
  ) {
    return null;
  }

  return (
    <>
      <div className="rounded-lg border border-border-secondary bg-bg-primary overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6">
          <div className="flex flex-col gap-1 min-w-0">
            {label && (
              <p className="txt-medium-medium text-text-primary">{label}</p>
            )}
            {description && (
              <p className="txt-small-regular text-text-secondary">
                {description}
              </p>
            )}
            {minError && (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-text-error">
                <Icons.AlertCircle className="h-4 w-4 shrink-0" />
                {minError}
              </p>
            )}
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={openAdd}
            disabled={isAtMax}
          >
            <Icons.Plus className="w-4 h-4" />
            {buttonText}
          </Button>
        </div>

        {/* Entry list */}
        {entries.length > 0 && (
          <div className="px-6 pb-6">
            {entries.map((entry, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 py-4 ${
                  index < entries.length - 1
                    ? 'border-b border-border-secondary'
                    : ''
                }`}
              >
                <p className="flex-1 txt-small-medium text-text-primary truncate">
                  {getEntryLabel(entry)}
                </p>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-bg-secondary transition-colors shrink-0"
                  onClick={() => openEdit(index)}
                  title="Edit"
                >
                  <Icons.Edit03 className="w-4 h-4 text-text-tertiary" />
                </button>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-bg-secondary transition-colors shrink-0"
                  onClick={() => handleDelete(index)}
                  title="Delete"
                >
                  <Icons.Trash01 className="w-4 h-4 text-text-tertiary" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <NestedFormDrawer isOpen={open} close={handleClose}>
        {isLoading && (
          <div className="flex items-center justify-center py-12 h-full">
            <LoadingIndicator />
          </div>
        )}

        {templateError && (
          <div className="text-center py-12 h-full flex flex-col items-center justify-center">
            <Icons.AlertCircle className="w-12 h-12 text-text-error mx-auto mb-4" />
            <p className="txt-medium-medium text-text-primary mb-2">
              Failed to load form
            </p>
            <p className="txt-small-regular text-text-tertiary">
              {templateKey && !templateKey.liveFormId
                ? 'This template key does not have an active live form.'
                : 'The linked form template could not be loaded.'}
            </p>
          </div>
        )}

        {template && !isLoading && !templateError && (
          <LinkedFormRenderer
            dsl={template.dsl}
            type={template.type as FormsTemplateType}
            defaultValues={defaultValues}
            onClose={handleClose}
            onSubmit={handleSubmit}
            layoutSettings={{
              hideTopHeader: true,
            }}
            embedMode
            componentRegistry={componentRegistry}
          />
        )}
      </NestedFormDrawer>
    </>
  );
};
